import { useEffect, useRef } from 'react'
import { useFlag } from '@afterpaytouch/feature'
import { ConvergenceAuthMethod } from '../../model'
import { Features } from '../../model/features'
import { useJourneyId } from '../../state/cashAuth/hooks'
import { CASH_AUTH_SIGNALS, USER_JOURNEY_NAMES, USER_JOURNEY_TIMEOUT_MS } from './constants'
import { markJourneyMarkerHandoff, readJourneyMarker } from './journeyMarker'
import { JourneyOutcome, JourneyTerminationReason } from './types'
import { useUserJourneyContext } from './UserJourneyContext'

let grantHandoffPending = false

export const beginCashGrantHandoff = (): void => {
  grantHandoffPending = true
  markJourneyMarkerHandoff()
  window.addEventListener(
    'pageshow',
    (event) => {
      if (event.persisted) {
        grantHandoffPending = false
      }
    },
    { once: true }
  )
}

export const isCashGrantHandoffPending = (): boolean => grantHandoffPending

export const resetCashGrantHandoffForTesting = (): void => {
  grantHandoffPending = false
}

const CASH_AUTH_METHOD_TAGS: Record<ConvergenceAuthMethod, string> = {
  [ConvergenceAuthMethod.MobileRedirect]: 'mobile-redirect',
  [ConvergenceAuthMethod.DesktopRedirect]: 'desktop-redirect',
  [ConvergenceAuthMethod.QrCode]: 'qr-code',
}

export interface CashAuthJourneyAnchorProps {
  isAuthorized: boolean
  isDeclined: boolean
  isPollingLimitExceeded: boolean
  cashAuthMethod?: ConvergenceAuthMethod
}

export const useCashAuthJourneyAnchors = ({ isAuthorized, isDeclined, isPollingLimitExceeded, cashAuthMethod }: CashAuthJourneyAnchorProps): void => {
  const enabled = useFlag(Features.USER_JOURNEY_INSTRUMENTATION)
  const { active, addFriction, addTag, completeJourney, resumeJourney } = useUserJourneyContext()
  const cashAuthJourneyId = useJourneyId()
  const resumed = useRef(false)

  const journeyActive = active?.name === USER_JOURNEY_NAMES.CHECKOUT

  useEffect(() => {
    if (!enabled || resumed.current) {
      return
    }
    resumed.current = true
    const marker = readJourneyMarker()
    if (marker?.handoff === true && marker.journey.name === USER_JOURNEY_NAMES.CHECKOUT && !journeyActive) {
      resumeJourney(marker.journey, marker.esid)
      if (isDeclined && !marker.journey.frictions.includes(CASH_AUTH_SIGNALS.DECLINED)) {
        addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.DECLINED)
      }
      if (Date.now() - marker.journey.created > USER_JOURNEY_TIMEOUT_MS) {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, JourneyTerminationReason.TIMEOUT_WITH_IDLE_USER)
      }
    }
  }, [enabled, journeyActive, resumeJourney, isDeclined, addFriction, completeJourney])

  useEffect(() => {
    if (isAuthorized || isDeclined) {
      grantHandoffPending = false
    }
  }, [isAuthorized, isDeclined])

  useEffect(() => {
    if (!journeyActive || active === undefined) {
      return
    }
    const tagOnce = (key: string, value: string | undefined): void => {
      if (value === undefined || value === '' || active.tags.some((tag) => tag.startsWith(`${key}:`))) {
        return
      }
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
    }
    tagOnce('cashAuthJourneyId', cashAuthJourneyId)
    tagOnce('cashAuthMethod', cashAuthMethod === undefined ? undefined : CASH_AUTH_METHOD_TAGS[cashAuthMethod])
    if (isAuthorized) {
      tagOnce('cashAuthResult', 'authorized')
    }
  }, [journeyActive, active, addTag, cashAuthJourneyId, cashAuthMethod, isAuthorized])

  useEffect(() => {
    if (isDeclined && journeyActive && active?.frictions.includes(CASH_AUTH_SIGNALS.DECLINED) === false) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.DECLINED)
    }
  }, [isDeclined, journeyActive, active, addFriction])

  useEffect(() => {
    if (isPollingLimitExceeded && journeyActive) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.TIMEOUT)
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, JourneyTerminationReason.TIMEOUT_WITH_IDLE_USER)
    }
  }, [isPollingLimitExceeded, journeyActive, addFriction, completeJourney])
}
